// ============================================================
// Die Bausteine. Uebernommen aus /opt/lilasprojekte/frontend/src/components/ui/
// primitives.tsx (dort aus dem Zeitplaner, dort aus fragenbuero, dort aus webgis).
// Geaendert nach SPEC §13.2: `Avatar` ohne Buerofarbe (der Ring kommt aus der
// Nutzerkennung), `PhasenChip`, `KonzeptBalken` und `Tage` fallen weg, neu sind
// `StatusChip` (vier Status, §13.3) und `LizenzPlakette`.
//
// Die Regel dieser Datei: JEDE Groesse kommt aus einer Klasse in base.css, nicht
// aus einem Inline-Stil. Nur so greift die Telefonfassung (44 px Tippflaeche,
// 16 px Schriftgroesse gegen den iOS-Zoom), die dort in einer Medienabfrage steht.
// ============================================================
import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ChangeEvent,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import type { IconWeight } from "@phosphor-icons/react";
import type { Lizenz, Status } from "../../api/types";
import { istApiFehler } from "../../api/client";
import { useT } from "../../lib/texte";
import { Icon } from "./Icon";

/** Wohin Modale, Auswahllisten und Meldungen gezeichnet werden. */
export function portalRoot(): HTMLElement {
  return document.getElementById("root") || document.body;
}

/* ------------------------------------------------------------------ Warten */

export function Spinner({ size = 16, color }: { size?: number; color?: string }) {
  return (
    <span
      className="spin"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        border: `2px solid ${color || "var(--field-border)"}`,
        borderTopColor: color ? "transparent" : "var(--accent-text)",
        display: "inline-block",
        flex: "none",
      }}
    />
  );
}

/* ------------------------------------------------------------------ Knöpfe */

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "ghost" | "subtle" | "danger";
  /** Name aus der Icon-Registry; steht links vom Text. */
  icon?: string;
  iconWeight?: IconWeight;
  /** sm 34 px (Werkzeugleisten) · md 36 px (Vorgabe) · lg 46 px (Absenden) */
  size?: "sm" | "md" | "lg";
  /** React 19: `ref` ist eine gewöhnliche Prop und landet über `rest` am <button>. */
  ref?: Ref<HTMLButtonElement>;
}

/**
 * 36 px hoch, Radius 12 px, Gewicht 500 — die Werte der Entwurfsmappe.
 * `type` ist mit Absicht NICHT vorbelegt: ein Knopf in einem Formular ist
 * standardmäßig ein Absendeknopf, und genau das ist in den meisten Fällen
 * gemeint. Wer das nicht will, schreibt `type="button"`.
 */
export function Button({ variant = "primary", icon, iconWeight, children, className = "", size = "md", ...rest }: ButtonProps) {
  const groesse = size === "md" ? "" : ` btn-${size}`;
  return (
    <button className={`btn btn-${variant}${groesse} ring-focus ${className}`} {...rest}>
      {icon && <Icon name={icon} weight={iconWeight} size={size === "sm" ? 15 : 17} />}
      {children}
    </button>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: string;
  weight?: IconWeight;
  /** Kantenlänge des Symbols */
  size?: number;
  /** Kantenlänge der Trefferfläche; 36 px wie in der Mappe */
  box?: number;
  /** eingeschaltet — Akzentfläche statt durchsichtig */
  active?: boolean;
  /** umrandete Fassung: steht allein neben einer Überschrift */
  umrandet?: boolean;
}

/**
 * `title` wird AUCH zum `aria-label`: ein Knopf, der nur ein Symbol trägt,
 * heißt für eine Vorlesehilfe sonst „Schaltfläche" und sonst nichts.
 */
export function IconButton({ icon, weight, size = 18, box = 36, active, umrandet, title, className = "", style = {}, ...rest }: IconButtonProps) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      className={`icon-btn ring-focus${umrandet ? " umrandet" : ""} ${className}`}
      style={{
        width: box,
        height: box,
        background: active ? "var(--accent-soft)" : undefined,
        color: active ? "var(--accent-text)" : undefined,
        ...style,
      }}
      {...rest}
    >
      <Icon name={icon} weight={weight} size={size} />
    </button>
  );
}

/* ------------------------------------------------------------------- Chips */

export interface ChipProps {
  children?: ReactNode;
  /** eingeschaltet (Akzentfläche, Akzentring, Akzenttext) */
  on?: boolean;
  /** Name aus der Icon-Registry, links vom Text */
  icon?: string;
  /** Punkt in dieser Farbe statt eines Symbols — die Bürofarbe der Mappe */
  punkt?: string;
  title?: string;
  className?: string;
  style?: CSSProperties;
  /** mit Rückruf wird der Chip ein <button>, ohne bleibt er ein <span> */
  onClick?: () => void;
  disabled?: boolean;
}

/**
 * 26 px hoch, Radius 999 px. Ein Chip OHNE `onClick` ist eine Plakette und
 * kein Knopf — er wird dann als `<span>` gezeichnet, damit die Tabulatorreise
 * nicht über zwei Dutzend Beschriftungen führt.
 */
export function Chip({ children, on, icon, punkt, title, className = "", style, onClick, disabled }: ChipProps) {
  const inhalt = (
    <>
      {punkt && <span className="dot" style={{ background: punkt }} />}
      {icon && <Icon name={icon} size={12} />}
      {children}
    </>
  );
  const klasse = `chip${on ? " on" : ""}${onClick ? " chip-btn ring-focus" : ""} ${className}`;
  if (!onClick) {
    return (
      <span className={klasse} title={title} style={style}>
        {inhalt}
      </span>
    );
  }
  return (
    <button type="button" className={klasse} title={title} aria-pressed={on} disabled={disabled} onClick={onClick} style={style}>
      {inhalt}
    </button>
  );
}

/* ------------------------------------------------------------------ Felder */

/** Native Felder, die ein `<label for>` erreicht (HTML „labelable elements"). */
const NATIV_VERBINDBAR = new Set<unknown>(["input", "textarea", "select", "button"]);

/** Ist das einzige Kind ein Feld, das ein Label tragen kann? `Input`, `Textarea` und
 *  `Select` reichen `id` an ihr `<input>`/`<textarea>`/den Auslöser-Knopf weiter. Ein
 *  anderes Kind (Zeile mit zwei Feldern, Suchbaustein) bleibt unverbunden: ein `for` auf
 *  ein `<div>` meldet Chrome als falsch benutztes Label — dann lieber `aria-label` am Feld. */
function verbindbar(kind: ReactNode): kind is ReactElement<{ id?: string }> {
  return isValidElement(kind) && (NATIV_VERBINDBAR.has(kind.type) || kind.type === Input || kind.type === Textarea || kind.type === Select);
}

/**
 * Label + Feld + Hinweis. Das Label gehört per `htmlFor` zum Feld (§13.10-5, INTEGRATION
 * P7-13): ohne die Verbindung meldet Lighthouse „Form elements do not have associated
 * labels", und ein Klick aufs Label setzt den Fokus nicht. Die Kennung kommt aus `useId`;
 * trägt das Feld schon eine eigene `id`, gilt die.
 */
export function Field({ label, hint, children, className = "" }: { label?: string; hint?: string; children: ReactNode; className?: string }) {
  const eigeneId = useId();
  // Kein Alias wie `const verbinden = …`: die Verengung auf ReactElement soll ohne
  // TS-Feinheiten (Verengung über Alias-Bedingungen) halten.
  const feld = label && verbindbar(children) ? children : null;
  const feldId = feld ? (feld.props.id ?? eigeneId) : undefined;
  return (
    <div className={className}>
      {label && (
        <label className="flabel" htmlFor={feldId}>
          {label}
        </label>
      )}
      {feld ? cloneElement(feld, { id: feldId }) : children}
      {hint && <p className="hinweiszeile" style={{ marginTop: 6 }}>{hint}</p>}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Name aus der Icon-Registry; das Symbol steht IM Feld, links */
  icon?: string;
  mono?: boolean;
  /**
   * Seit React 19 ist `ref` eine gewöhnliche Prop einer Funktionskomponente —
   * `forwardRef` braucht es dafür nicht mehr. Sie landet über ...rest ohnehin
   * am <input>; hier steht sie nur, damit der Typ sie zulässt. Ohne diese
   * Zeile scheitert jeder Aufrufer, der das Feld fokussieren will, an TS2322,
   * obwohl es zur Laufzeit funktioniert hätte.
   */
  ref?: Ref<HTMLInputElement>;
}

export function Input({ icon, className = "", mono, style, ...rest }: InputProps) {
  if (icon)
    return (
      <div style={{ position: "relative" }}>
        <Icon
          name={icon}
          size={16}
          style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-mute)", pointerEvents: "none" }}
        />
        <input className={`inp ${mono ? "mono" : ""} ${className}`} style={{ paddingLeft: 36, ...style }} {...rest} />
      </div>
    );
  return <input className={`inp ${mono ? "mono" : ""} ${className}`} style={style} {...rest} />;
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  mono?: boolean;
  ref?: Ref<HTMLTextAreaElement>;
  /** `false` = feste Höhe aus `rows` (Vorgabe: wächst mit dem Inhalt) */
  wachsen?: boolean;
}

/** Höhe = Inhalt, mindestens `rows` Zeilen. Erst `height: auto`, sonst schrumpft das
 *  Feld nach dem Löschen nie; die 2 px sind der obere und untere Rand (border-box). */
export function hoeheAnpassen(el: HTMLTextAreaElement) {
  el.style.height = "auto";
  // Versteckt (Reiter zu, Dialog zu) misst der Browser 0 — dann bleibt `auto`, also
  // `rows`; sobald das Feld sichtbar wird, meldet der Breitenbeobachter und es misst neu.
  if (el.scrollHeight > 0) el.style.height = `${el.scrollHeight + 2}px`;
}

/**
 * Mehrzeiliges Feld, das mit seinem Inhalt wächst (Inhaber 08.10.: „auto resize the text
 * fields according to their content"). Gemessen wird vor dem Zeichnen (kein Springen),
 * nach jeder Wertänderung und wenn sich die Breite ändert — ein schmaleres Fenster bricht
 * dieselben Zeilen öfter um. `rows` bleibt die Mindesthöhe.
 */
export function Textarea({ className = "", mono, rows = 4, style, wachsen = true, ref, value, ...rest }: TextareaProps) {
  const eigen = useRef<HTMLTextAreaElement | null>(null);
  const setzeRef = useCallback(
    (el: HTMLTextAreaElement | null) => {
      eigen.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) (ref as { current: HTMLTextAreaElement | null }).current = el;
    },
    [ref],
  );
  useLayoutEffect(() => {
    if (wachsen && eigen.current) hoeheAnpassen(eigen.current);
  }, [wachsen, value]);
  useEffect(() => {
    const el = eigen.current;
    if (!wachsen || !el || typeof ResizeObserver === "undefined") return;
    let breite = el.clientWidth;
    const beobachter = new ResizeObserver(() => {
      // Nur auf die Breite reagieren: die eigene Höhenänderung meldet der Beobachter auch.
      if (el.clientWidth === breite) return;
      breite = el.clientWidth;
      hoeheAnpassen(el);
    });
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, [wachsen]);
  return (
    <textarea
      ref={setzeRef}
      rows={rows}
      value={value}
      className={`inp ${mono ? "mono" : ""} ${wachsen ? "inp-wachsend" : ""} ${className}`}
      style={style}
      {...rest}
    />
  );
}

/* ---------------------------------------------------------------- Auswahl */

/** Luft zwischen der aufgeklappten Liste und dem Fensterrand. */
const RAND = 8;

/** Obergrenze der Liste — darüber liest sich eine Zeile nicht mehr gut. */
const BREITESTE_LISTE = 460;

export type SelectOption = string | { value: string; label: string };

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "value" | "onChange"> {
  value: string | null | undefined;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  options: SelectOption[];
  placeholder?: string;
  /** schmalerer Auslöser, damit er in einer Kopfzeile mitläuft */
  compact?: boolean;
  /** Filterfeld über der Liste; ab 12 Einträgen von selbst an */
  searchable?: boolean;
}

/**
 * Eigenes Auswahlfeld statt `<select>`: die aufgeklappte Liste eines nativen
 * `<select>` zeichnet das Betriebssystem, sie lässt sich weder runden noch
 * einfärben — und in dieser Oberfläche steht sie neben Chips und Knöpfen, die
 * beide gerundet sind. Die Schnittstelle ist die des nativen Feldes:
 * `onChange` bekommt weiterhin ein Ereignis der Form `{target: {value}}`.
 */
export function Select({
  value, onChange, options, className = "", placeholder, disabled, style, compact, searchable,
  // Die NAMENSGEBENDEN Attribute wandern auf den Auslöser-Knopf. `SelectProps`
  // erweitert `SelectHTMLAttributes`, der TYP nimmt sie also an — ohne diese
  // Zeilen fielen sie zur Laufzeit lautlos weg, und ein Auswahlfeld ohne Namen
  // heißt für eine Vorlesehilfe nur „…, Schaltfläche". Bewusst EINZELN
  // aufgezählt und nicht als `...rest` gespreizt: `SelectHTMLAttributes` trägt
  // Dinge wie `multiple`, `size` und `required`, die ein `<button>` nicht kennt.
  id, title, name,
  "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby, "aria-describedby": ariaDescribedby,
}: SelectProps) {
  const norm = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const [offen, setOffen] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState<{ left: number; top: number; width: number; auf: boolean } | null>(null);
  const knopfRef = useRef<HTMLButtonElement>(null);
  const listeRef = useRef<HTMLDivElement>(null);
  const { t } = useT();
  const aktuell = norm.find((o) => o.value === (value ?? ""));
  const mitSuche = searchable ?? norm.length > 12;
  const nadel = q.trim().toLowerCase();
  const sichtbar = nadel ? norm.filter((o) => `${o.label} ${o.value}`.toLowerCase().includes(nadel)) : norm;

  const platzieren = useCallback(() => {
    const r = knopfRef.current?.getBoundingClientRect();
    if (!r) return;
    const maxH = Math.min(264, norm.length * 38 + 12) + (mitSuche ? 46 : 0);
    const auf = r.bottom + maxH + 8 > window.innerHeight && r.top - maxH - 8 > 0;
    setPos({ left: r.left, top: auf ? r.top - 4 : r.bottom + 4, width: r.width, auf });
  }, [norm.length, mitSuche]);

  useLayoutEffect(() => {
    if (offen) {
      setQ("");
      platzieren();
    }
  }, [offen, platzieren]);

  // Die Liste ist so breit wie ihr längster Eintrag (`max-content`), mindestens
  // so breit wie das Feld. Deshalb kann sie rechts hinausragen — dann rückt sie
  // nach links, so weit wie nötig und nie über den linken Rand hinaus. Erst
  // NACH dem Zeichnen, weil vorher niemand weiß, wie breit sie wird.
  useLayoutEffect(() => {
    const el = listeRef.current;
    if (!offen || !el || !pos) return;
    const b = el.getBoundingClientRect();
    const zuviel = b.right - (window.innerWidth - RAND);
    if (zuviel <= 0.5) return;
    const links = Math.max(RAND, pos.left - zuviel);
    // Die halbe Pixelbreite hält die Schleife an: ohne sie liefe das Rücken
    // ewig weiter, weil `getBoundingClientRect` gebrochene Werte liefert.
    if (Math.abs(links - pos.left) > 0.5) setPos({ ...pos, left: links });
  }, [offen, pos]);

  useEffect(() => {
    if (!offen) return;
    // Schließen nur bei Klick daneben oder Escape — Rollen und Größenänderung
    // RÜCKEN bloß nach, damit die Liste am Feld klebt (wie ein natives
    // <select>), statt zuzuschnappen.
    const beiDruck = (e: Event) => {
      if (listeRef.current?.contains(e.target as Node) || knopfRef.current?.contains(e.target as Node)) return;
      setOffen(false);
    };
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Die offene Liste verbraucht das Escape VOLLSTÄNDIG — sonst schlösse
      // dasselbe Escape auch ein Modal darüber und würfe dessen Eingaben weg.
      e.stopPropagation();
      setQ((cur) => {
        if (cur) return "";
        setOffen(false);
        return cur;
      });
    };
    const beiRollen = (e: Event) => {
      if (listeRef.current?.contains(e.target as Node)) return;
      platzieren();
    };
    document.addEventListener("mousedown", beiDruck);
    document.addEventListener("keydown", beiTaste);
    document.addEventListener("scroll", beiRollen, true);
    window.addEventListener("resize", platzieren);
    return () => {
      document.removeEventListener("mousedown", beiDruck);
      document.removeEventListener("keydown", beiTaste);
      document.removeEventListener("scroll", beiRollen, true);
      window.removeEventListener("resize", platzieren);
    };
  }, [offen, platzieren]);

  const waehlen = (v: string) => {
    onChange({ target: { value: v } } as unknown as ChangeEvent<HTMLSelectElement>);
    setOffen(false);
    knopfRef.current?.focus();
  };

  return (
    <div style={{ position: "relative", ...style }}>
      <button
        ref={knopfRef}
        type="button"
        disabled={disabled}
        className={`inp${compact ? " inp-compact" : ""} ${className}`}
        id={id}
        // Im Feld selbst bleibt es beim Abschneiden — dafür ist es zu schmal.
        // Der gewählte Eintrag steht aber im Tooltip, wenn nicht ohnehin ein
        // eigener gesetzt ist.
        title={title ?? aktuell?.label}
        name={name}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-describedby={ariaDescribedby}
        aria-haspopup="listbox"
        aria-expanded={offen}
        onClick={() => !disabled && setOffen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          textAlign: "left",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.55 : 1,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: aktuell ? "var(--text)" : "var(--text-mute)" }}>
          {aktuell?.label ?? placeholder ?? ""}
        </span>
        <Icon name="caret-down" size={14} style={{ color: "var(--text-mute)", flex: "none", transition: "transform .15s", transform: offen ? "rotate(180deg)" : "none" }} />
      </button>
      {offen &&
        pos &&
        createPortal(
          <div
            ref={listeRef}
            className="scroll anim-pop"
            role="listbox"
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.auf ? undefined : pos.top,
              bottom: pos.auf ? window.innerHeight - pos.top : undefined,
              // Nicht `width: pos.width`: dann schnitte ein schmales Feld jeden
              // längeren Eintrag ab („Landschaftspflegerischer Beglei…"). Das
              // Feld gibt nur noch das MINDESTmaß; gewachsen wird nach Inhalt.
              minWidth: pos.width,
              width: "max-content",
              maxWidth: Math.min(BREITESTE_LISTE, window.innerWidth - 2 * RAND),
              maxHeight: 264,
              overflowY: "auto",
              borderRadius: "var(--r-knopf)",
              padding: 5,
              zIndex: 1000,
              background: "var(--panel-solid)",
              border: "1px solid var(--glass-border-strong)",
              boxShadow: "var(--shadow-lg)",
            }}
          >
            {mitSuche && (
              <div style={{ position: "sticky", top: 0, zIndex: 1, padding: "1px 1px 6px", background: "var(--panel-solid)" }}>
                <Input
                  icon="magnifying-glass"
                  autoFocus
                  value={q}
                  placeholder={t.allgemein.suchen}
                  aria-label={t.allgemein.suchen}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter auf einem eindeutigen Treffer wählt ihn direkt.
                    const eins = sichtbar[0];
                    if (e.key === "Enter" && sichtbar.length === 1 && eins) waehlen(eins.value);
                  }}
                  style={{ padding: "7px 10px 7px 34px", fontSize: 13, borderRadius: "var(--r-knopf)", height: 34 }}
                />
              </div>
            )}
            {mitSuche && sichtbar.length === 0 && (
              <div style={{ padding: "10px 12px", fontSize: 12.5, color: "var(--text-mute)" }}>{t.allgemein.keineTreffer}</div>
            )}
            {sichtbar.map((o) => {
              const an = o.value === (value ?? "");
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={an}
                  onClick={() => waehlen(o.value)}
                  className="wahlzeile"
                  style={{
                    fontWeight: an ? 600 : 500,
                    background: an ? "var(--accent-soft)" : "transparent",
                    color: an ? "var(--accent-text)" : "var(--text-dim)",
                  }}
                >
                  {an ? <Icon name="check" size={13} /> : <span style={{ width: 13, flex: "none" }} />}
                  {/* `1 1 auto` statt `1`: die Basis ist der Text selbst, nicht
                      0. Chrome misst `max-content` nachgeprüft in BEIDEN
                      Fassungen gleich — die Liste wird also so oder so breit
                      genug. Die Basis am Text steht hier, weil sie beim
                      Ausmessen keine Auslegungssache ist; ausprobiert ist das
                      aber nur in Chrome. Gewachsen wird wie vorher. */}
                  <span style={{ flex: "1 1 auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</span>
                </button>
              );
            })}
          </div>,
          portalRoot(),
        )}
    </div>
  );
}

/* ---------------------------------------------------------------- Schalter */

export function Switch({ on, onChange, disabled, label }: { on: boolean; onChange?: (v: boolean) => void; disabled?: boolean; label?: string }) {
  return (
    <button
      type="button"
      className={`io ${on ? "on" : ""} ring-focus`}
      role="switch"
      aria-checked={!!on}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange && onChange(!on)}
      style={{ opacity: disabled ? 0.5 : 1, border: 0, padding: 0 }}
    >
      <span className="knob" />
    </button>
  );
}

/* ------------------------------------------------------------------ Avatar */

/**
 * Das Wenige, das ein Avatar von einer Person wissen muss — ein `Ich` und ein
 * `PersonKurz` (§15.4) passen beide.
 */
export interface AvatarPerson {
  id: string;
  anzeigename: string | null;
  kuerzel: string;
}

export interface AvatarProps {
  person: AvatarPerson;
  size?: number;
  /** Ring in der Farbe der Person (aus ihrer Kennung gerechnet, §13.2) */
  ring?: boolean;
  /** Ringfarbe von Hand */
  farbe?: string;
  title?: string;
  style?: CSSProperties;
  onClick?: () => void;
}

/** oklch → #rrggbb (dieselbe Rechnung wie `.design/lib.mjs`, damit Canvas und App
 *  dieselben Farben zeigen). */
export function oklchHex(L: number, C: number, hGrad: number): string {
  const h = (hGrad * Math.PI) / 180;
  const a = C * Math.cos(h);
  const b = C * Math.sin(h);
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const linear = [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
  return (
    "#" +
    linear
      .map((v) => {
        const c = Math.min(1, Math.max(0, v));
        const g = c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
        return Math.round(g * 255)
          .toString(16)
          .padStart(2, "0");
      })
      .join("")
  );
}

/**
 * Die Ringfarbe einer Person. Gerechnet aus ihrer Kennung (Paket D, `.design/lib.mjs`
 * `ringFarbe`: Hash → Farbton, oklch(0.70 0.12 h)), damit dieselbe Person auf jedem
 * Bildschirm, bei jedem Laden und im Canvas dieselbe Farbe trägt — ohne Bürofarbe wie
 * in projekte (§13.2) und ohne eine Spalte, die jemand pflegen müsste.
 */
export function ringfarbe(id: string): string {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) % 360;
  return oklchHex(0.7, 0.12, (h * 47) % 360);
}

/**
 * Die Buchstaben im Kreis: das Kürzel, sonst die Initialen des Anzeigenamens
 * (§2.5 — das Kürzel ist freiwillig).
 */
export function initialen(person: Pick<AvatarPerson, "anzeigename" | "kuerzel">): string {
  const k = person.kuerzel.trim();
  if (k) return k.slice(0, 4).toUpperCase();
  const teile = (person.anzeigename ?? "").trim().split(/[\s.\-_]+/).filter(Boolean);
  if (teile.length === 0) return "?";
  if (teile.length === 1) return (teile[0] ?? "?").slice(0, 2).toUpperCase();
  return ((teile[0]?.[0] ?? "") + (teile[teile.length - 1]?.[0] ?? "")).toUpperCase();
}

/**
 * Kreis mit Kürzel oder Initialen. Der Ring liegt als DOPPELTER box-shadow außen an
 * (2 px Farbe, danach 1,5 px Grundfarbe), damit er auch vor einem gleichfarbigen
 * Untergrund steht.
 */
export function Avatar({ person, size = 30, ring, farbe, title, style, onClick }: AvatarProps) {
  const name = title ?? person.anzeigename ?? person.kuerzel;
  const gemeinsam: CSSProperties = {
    width: size,
    height: size,
    fontSize: Math.round(size * 0.37),
    boxShadow: ring ? `0 0 0 2px ${farbe ?? ringfarbe(person.id)}, 0 0 0 3.5px var(--panel-solid)` : undefined,
    cursor: onClick ? "pointer" : undefined,
    ...style,
  };
  const inhalt = initialen(person);
  if (onClick) {
    return (
      <button type="button" className="av ring-focus" style={{ ...gemeinsam, border: 0, padding: 0 }} title={name} aria-label={name} onClick={onClick}>
        {inhalt}
      </button>
    );
  }
  return (
    <span className="av" style={gemeinsam} title={name} role="img" aria-label={name}>
      {inhalt}
    </span>
  );
}

/* ---------------------------------------------------------- Statusplakette */

const STATUS_KLASSE: Record<Status, string> = {
  offen: "s-offen",
  "in Abstimmung": "s-abstimmung",
  abgestimmt: "s-abgestimmt",
  entfallen: "s-entfallen",
};

/**
 * Der Status einer Karte (§13.3). Das Wort steht IMMER mit da: eine Plakette, die
 * nur über die Farbe spricht, ist für einen Teil des Büros grau. Die Wörter sind
 * die Werte selbst (`offen`, `in Abstimmung` …) — sie stehen so auch im Stempel.
 */
export function StatusChip({ status, title, style }: { status: Status; title?: string; style?: CSSProperties }) {
  return (
    <span className={`status-chip ${STATUS_KLASSE[status]}`} title={title} style={style}>
      {status}
    </span>
  );
}

/* ---------------------------------------------------------- Lizenzplakette */

/** Die Rechte eines Bildes (§13.3). `unbekannt` warnt — im Abgabe-Modus wird so ein
 *  Bild zum Platzhalter (§5.6). */
export function LizenzPlakette({ lizenz, style }: { lizenz: Lizenz; style?: CSSProperties }) {
  const { t } = useT();
  const symbol = lizenz === "unbekannt" ? "warning" : lizenz === "angefragt" ? "clock" : "check";
  return (
    <span className={`lizenz-plakette l-${lizenz}`} title={t.lizenz.hinweis[lizenz]} style={style}>
      <Icon name={symbol} size={12} />
      {t.lizenz.name[lizenz]}
    </span>
  );
}

/* ------------------------------------------------------------- Leerzustand */

export function Leer({ icon, title, sub, aktion }: { icon: string; title: string; sub?: string; aktion?: ReactNode }) {
  return (
    <div className="leer">
      <Icon name={icon} weight="light" size={46} style={{ opacity: 0.5, margin: "0 auto" }} />
      <p className="leer-titel">{title}</p>
      {sub && <p style={{ fontSize: 13, marginTop: 5 }}>{sub}</p>}
      {aktion && <div className="reihe wrap" style={{ justifyContent: "center", marginTop: 18 }}>{aktion}</div>}
    </div>
  );
}

/**
 * Fehlerzustand einer Abfrage: der Satz des Servers (oder der allgemeine) und, wenn
 * `erneut` gesetzt ist, „Erneut versuchen". Ohne ihn blieben Listen nach einem 500 leer
 * oder zeigten eine Null, als gäbe es nichts (Prüfung Runde 1: Stände, Nutzer, Bibliothek).
 */
export function LadeFehler({ fehler, erneut }: { fehler: unknown; erneut?: () => void }) {
  const { t } = useT();
  return (
    <Leer
      icon="warning-circle"
      title={istApiFehler(fehler) ? fehler.detail : t.fehler.ladenFehlgeschlagen}
      aktion={
        erneut ? (
          <Button type="button" variant="ghost" icon="arrow-clockwise" onClick={erneut}>
            {t.fehler.erneut}
          </Button>
        ) : undefined
      }
    />
  );
}

/* --------------------------------------------------------------- Glasfläche */

export interface GlasProps {
  children: ReactNode;
  /** deckender und stärker verwischt — über Seitenbildern und auf dem Telefon */
  stark?: boolean;
  /** Radius 20 px ist die Vorgabe; Werkzeugleisten nehmen 16 px */
  radius?: number;
  className?: string;
  style?: CSSProperties;
}

/** Die schwebende Fläche (Kopfleiste, Unterleiste, Werkzeuge). */
export function Glas({ children, stark, radius, className = "", style }: GlasProps) {
  return (
    <div className={`${stark ? "glass-strong" : "glass"} ${className}`} style={radius === undefined ? style : { borderRadius: radius, ...style }}>
      {children}
    </div>
  );
}
